import { UserTag } from '@kcftech/prometheus-client';
import { useMutation } from '@tanstack/react-query';
import { MultiSelectOption } from 'components/forms/multiselect/MultiSelect';
import { MultiSelectInlineEditor } from 'components/inline_edit/MultiSelectInlineEditor';
import { useNotificationContext } from 'contexts/NotificationContext';
import { usePermissions } from 'contexts/PermissionsContext';
import { sdClient } from 'contexts/SDKClientContext';
import { useUserTags, userTagsQueryKey } from 'custom_hooks/data_hooks/user/useUserTags';
import { queryClient } from 'utils/query';

const USER_TAG_OPTIONS: MultiSelectOption<UserTag>[] = [
    { title: 'Desk Chat', value: UserTag.DeskChat },
    { title: 'Critical Alarm', value: UserTag.CriticalAlarm },
    { title: 'QA', value: UserTag.Qa },
    { title: 'QA Impersonation Target', value: UserTag.QaImpersonationTarget },
];

export type UserTagsCellProps = Readonly<{
    userId: string;
}>;

export function UserTagsCell({ userId }: UserTagsCellProps) {
    const { canManageUserTags } = usePermissions();
    const { showToast } = useNotificationContext();
    const { data: currentTags } = useUserTags(userId);

    const currentTagSet = new Set(currentTags ?? []);

    const { mutateAsync: syncTags, isPending } = useMutation({
        meta: { name: 'sync-user-tags' },
        mutationFn: async (selectedTags: UserTag[]) => {
            const selectedSet = new Set(selectedTags);
            const toAssign = selectedTags.filter((t) => !currentTagSet.has(t));
            const toRemove = (currentTags ?? []).filter((t) => !selectedSet.has(t));

            await Promise.all([
                ...toAssign.map((tag) => sdClient.assignUserTag({ userId, tag })),
                ...toRemove.map((tag) => sdClient.removeUserTag({ userId, tag })),
            ]);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [userTagsQueryKey, userId],
            });
        },
        onError: () =>
            showToast({
                type: 'error',
                content: 'Failed to update user tags.',
            }),
    });

    return (
        <MultiSelectInlineEditor
            value={USER_TAG_OPTIONS.filter((opt) => currentTagSet.has(opt.value))}
            multiSelectConfig={{ options: USER_TAG_OPTIONS }}
            onConfirm={async (selected) => {
                if (selected !== undefined) {
                    await syncTags(selected.map((opt) => opt.value as UserTag));
                }
            }}
            isDisabled={!canManageUserTags || isPending || !currentTags}
        />
    );
}
